import Vue from "vue";
import store from "@/store";
import router from "@/router";
import axios from "axios";
import VueAxios from "vue-axios";
import { globalLoader } from "@/helpers/variables";
const camelcaseObjectDeep = require("camelcase-object-deep");

Vue.use(VueAxios, axios);

const TOKEN_KEY = "user_token";
// оновлюємо токен за 5 хв до exp, щоб користувач не ловив пачку 401 раз на добу
const REFRESH_LEAD_TIME = 5 * 60 * 1000;

// один спільний промис на весь застосунок: скільки б запитів не отримали 401,
// у мережу піде рівно один /auth/refresh
let refreshPromise = null;
let refreshTimer = null;
// вимикається, якщо годинник клієнта збився (див. runProactiveRefresh)
let proactiveDisabled = false;
let sessionDropped = false;

const getUserAuthToken = () => localStorage.getItem(TOKEN_KEY);

const currentLocale = () => {
  const language = store.getters.currentLanguage;
  return language ? language.translationKey : undefined;
};

export const $http = axios.create({
  baseURL: process.env.VUE_APP_ROOT_API,
  headers: {
    Accept: "application/json",
    "Access-Control-Allow-Headers": "*",
  },
});

$http.interceptors.request.use(config => {
  const token = getUserAuthToken();
  // Authorization виставляємо тільки тут: інакше після логауту в мережу
  // йшов би запечений при створенні інстансу старий токен
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  else delete config.headers["Authorization"];

  const locale = currentLocale();
  if (locale) config.headers["X-localization"] = locale;
  return config;
});

export const decodeToken = token => {
  try {
    const payload = String(token).split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch (e) {
    return null;
  }
};

// свідомо повз $http: інакше 401 на самому рефреші знову зайшов би
// у response-інтерсептор і спробував рефрешити ще раз
function requestNewToken(token) {
  return axios
    .post(
      `${process.env.VUE_APP_ROOT_API}v1/auth/refresh`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "X-localization": currentLocale(),
        },
      }
    )
    .then(response => camelcaseObjectDeep(response.data).data.accessToken);
}

// Повертає актуальний токен. staleToken — той, з яким запит отримав 401.
function ensureFreshToken(staleToken) {
  const stored = getUserAuthToken();

  if (!stored) return Promise.reject(new Error("NO_AUTH_TOKEN"));
  // сусідня вкладка вже все оновила — просто беремо її токен, свій рефреш не потрібен
  if (staleToken && stored !== staleToken) return Promise.resolve(stored);

  if (!refreshPromise) {
    const tokenAtStart = stored;

    const flight = requestNewToken(tokenAtStart)
      .then(accessToken => {
        store.commit("auth/SET_TOKEN", accessToken);
        return accessToken;
      })
      .catch(error => {
        // токен могла оновити інша вкладка, поки ми ходили по мережу:
        // тоді наша 400 нічого не означає і стирати чужий свіжий токен не можна
        const current = getUserAuthToken();
        if (current && current !== tokenAtStart) return current;
        throw error;
      });

    const release = () => {
      if (refreshPromise === flight) refreshPromise = null;
    };
    flight.then(release, release);

    refreshPromise = flight;
  }

  return refreshPromise;
}

function retryWithFreshToken(originalRequest, onRefreshFailed) {
  const authHeader = (originalRequest.headers && originalRequest.headers.Authorization) || "";
  const staleToken = String(authHeader).replace(/^Bearer\s+/, "");

  // onRefreshFailed навмисно другим аргументом .then, а не через .catch:
  // інакше ми б ловили ще й помилки самого повтореного запиту (404, 500)
  // і вважали їх втратою сесії
  return ensureFreshToken(staleToken).then(token => {
    originalRequest._retried = true;
    originalRequest.headers["Authorization"] = `Bearer ${token}`;
    // ретрай іде через $http, тож відповідь пройде звичайний response-інтерсептор
    return $http(originalRequest);
  }, onRefreshFailed);
}

function dropSession() {
  // REMOVE_TOKEN перезавантажує сторінку, тому викликаємо його рівно один раз
  if (sessionDropped) return;
  sessionDropped = true;
  clearTokenRefresh();
  router.push({ name: "main" }).catch(() => {});
  store.commit("auth/REMOVE_TOKEN");
}

export function clearTokenRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function isExpiringSoon(token) {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return null;
  return payload.exp * 1000 - Date.now() - REFRESH_LEAD_TIME <= 0;
}

function runProactiveRefresh(token) {
  ensureFreshToken(token).then(newToken => {
    // Щойно виданий токен уже виглядає протермінованим — значить збився годинник
    // клієнта. Вимикаємо проактивний рефреш, щоб не бити по бекенду в циклі;
    // реактивний шлях через 401 лишається.
    if (isExpiringSoon(newToken)) {
      proactiveDisabled = true;
      clearTokenRefresh();
    }
  }, () => {});
}

export function scheduleTokenRefresh() {
  clearTokenRefresh();
  if (proactiveDisabled) return;

  const token = getUserAuthToken();
  if (!token) return;

  const payload = decodeToken(token);
  if (!payload || !payload.exp) return;

  const delay = payload.exp * 1000 - Date.now() - REFRESH_LEAD_TIME;
  if (delay <= 0) {
    runProactiveRefresh(token);
    return;
  }

  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    const current = getUserAuthToken();
    if (current) runProactiveRefresh(current);
  }, Math.min(delay, 0x7fffffff));
}

// у сплячій вкладці setTimeout не спрацьовує вчасно, тому переперевіряємо при поверненні
function onTabWake() {
  if (proactiveDisabled) return;

  const token = getUserAuthToken();
  if (!token) return;

  const expiring = isExpiringSoon(token);
  if (expiring === null) return;

  if (expiring) runProactiveRefresh(token);
  else if (!refreshTimer) scheduleTokenRefresh();
}

document.addEventListener("visibilitychange", () => {
  if (!document.hidden) onTabWake();
});
window.addEventListener("focus", onTabWake);

$http.interceptors.response.use(
  response => {
    response.data = camelcaseObjectDeep(response.data);
    response.data &&
      response.data.adminData &&
      store.commit("profile/setAdminData", response.data.adminData);
    return response;
  },
  error => {
    const originalRequest = error.config;

    // мережева помилка, таймаут або скасований запит — відповіді немає
    if (!originalRequest || !error.response) return Promise.reject(error);

    if (originalRequest.hasOwnProperty("errorHandle") && originalRequest.errorHandle === false) {
      return Promise.reject(camelcaseObjectDeep(error.response));
    }

    switch (error.response.status) {
      case 503: {
        router.push({ name: "server-maintenance" }).catch(() => {});
        globalLoader(false);
        break;
      }
      case 500: {
        router.push({ name: "server-error" }).catch(() => {});
        globalLoader(false);
        break;
      }
      case 404: {
        router.push({ name: "page-not-found" }).catch(() => {});
        break;
      }
      case 403: {
        Vue.toasted.error("Доступ до сторінки чи ресурсу заборонено");
        break;
      }
      case 401: {
        // неавторизований користувач — рефрешити нічого, віддаємо помилку як є
        if (!getUserAuthToken()) break;

        if (!originalRequest._retried) {
          return retryWithFreshToken(originalRequest, () => {
            dropSession();
            return Promise.reject(camelcaseObjectDeep(error.response));
          });
        }

        dropSession();
        break;
      }
      default: {
        break;
      }
    }

    return Promise.reject(camelcaseObjectDeep(error.response));
  }
);

export default function install(Vue) {
  Object.defineProperty(Vue.prototype, "$http", {
    get() {
      return $http;
    },
  });
}
